/**
 * Villa Copa referral program.
 * Pure logic first (unit tested with node --test), browser wiring below.
 */

export const REF_KEY = 'vc_ref'
export const REF_WINDOW_DAYS = 30

const DAY_MS = 86400000
const REF_PATTERN = /^[A-Za-z0-9_-]{1,40}$/

export function parseRefCode(raw) {
  return typeof raw === 'string' && REF_PATTERN.test(raw) ? raw : null
}

export function getReferral(store, now) {
  try {
    const raw = store.getItem(REF_KEY)
    if (!raw) return null

    const { code, ts } = JSON.parse(raw)
    if (!parseRefCode(code) || typeof ts !== 'number') return null
    if (now - ts >= REF_WINDOW_DAYS * DAY_MS) return null

    return code
  } catch {
    return null
  }
}

export function captureReferral(search, store, now) {
  try {
    const code = parseRefCode(new URLSearchParams(search).get('ref'))
    if (!code || getReferral(store, now)) return

    store.setItem(REF_KEY, JSON.stringify({ code, ts: now }))
  } catch {
    // Storage blocked: attribution simply does not happen.
  }
}

export function estimateCommission({ nights, rate, percent }) {
  const n = Number(nights)
  const r = Number(rate)
  const p = Number(percent)

  if (!Number.isInteger(n) || n < 1 || n > 365) return null
  if (!Number.isFinite(r) || r <= 0 || r > 1000000) return null
  if (!Number.isFinite(p) || p <= 0 || p > 100) return null

  const subtotal = Math.round(n * r * 100) / 100
  const commission = Math.round(subtotal * p) / 100

  return { subtotal, commission }
}

/* ----------------------------------------------------------------------------------------------------------------
   Browser wiring (skipped when imported by Node tests)
---------------------------------------------------------------------------------------------------------------- */

function createStore() {
  try {
    const probe = '__vc_probe__'
    window.localStorage.setItem(probe, '1')
    window.localStorage.removeItem(probe)
    return window.localStorage
  } catch {
    // Fall through to cookie storage.
  }

  try {
    return {
      getItem(key) {
        const match = document.cookie.split('; ').find((part) => part.startsWith(`${key}=`))
        return match ? decodeURIComponent(match.slice(key.length + 1)) : null
      },
      setItem(key, value) {
        const maxAge = REF_WINDOW_DAYS * 86400
        document.cookie = `${key}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax`
      }
    }
  } catch {
    const memory = new Map()
    return {
      getItem: (key) => (memory.has(key) ? memory.get(key) : null),
      setItem: (key, value) => memory.set(key, String(value))
    }
  }
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  const store = createStore()

  captureReferral(window.location.search, store, Date.now())
  window.villaCopaReferral = { get: () => getReferral(store, Date.now()) }
}

if (typeof customElements !== 'undefined' && !customElements.get('referral-estimator')) {
  const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

  class ReferralEstimator extends HTMLElement {
    connectedCallback() {
      this.nightsInput = this.querySelector('input[data-nights]')
      this.rateInput = this.querySelector('input[data-rate]')
      this.subtotalEl = this.querySelector('[data-subtotal]')
      this.commissionEl = this.querySelector('[data-commission]')

      if (!this.nightsInput || !this.rateInput) return

      this.nightsInput.value = this.getAttribute('nights') || ''
      this.rateInput.value = this.getAttribute('rate') || ''

      const fallback = this.querySelector('[data-fallback]')
      if (fallback) fallback.hidden = true

      const interactive = this.querySelector('[data-interactive]')
      if (interactive) interactive.hidden = false

      this.nightsInput.addEventListener('input', () => this.update())
      this.rateInput.addEventListener('input', () => this.update())
      this.update()
    }

    update() {
      const result = estimateCommission({
        nights: this.nightsInput.value,
        rate: this.rateInput.value,
        percent: this.getAttribute('percent')
      })

      if (this.subtotalEl) this.subtotalEl.textContent = result ? usd.format(result.subtotal) : '—'
      if (this.commissionEl) this.commissionEl.textContent = result ? usd.format(result.commission) : '—'
    }
  }

  customElements.define('referral-estimator', ReferralEstimator)
}
